// Function to get _ttp cookie
function getTtpCookie() {
  if (typeof document === 'undefined')
    return null
  return document.cookie.split('; ').find(row => row.startsWith('_ttp='))?.split('=')[1] || null
}

function getFbpCookie() {
  if (typeof document === 'undefined')
    return null
  return document.cookie.split('; ').find(row => row.startsWith('_fbp='))?.split('=')[1] || null
}

function getFbcCookie() {
  if (typeof document === 'undefined')
    return null
  return document.cookie.split('; ').find(row => row.startsWith('_fbc='))?.split('=')[1] || null
}

export default defineNuxtPlugin(() => {
  const baseStore = useBaseStore()
  const userStore = useUserStore()
  const consentStore = useConsentStore()

  // Create a custom $fetch instance with default headers
  const customFetch = $fetch.create({
    onRequest({ request, options }) {
      // Get the URL string to check if it needs tracking cookies
      const urlString = typeof request === 'string' ? request : request.toString()
      const isGasStationsLiteRequest = urlString.includes('gas-stations-lite.json')

      // Special case: keep this request as a simple CORS GET (no custom headers),
      // so the browser does not trigger a preflight.
      if (isGasStationsLiteRequest) {
        return
      }

      // Check if this is a tracking endpoint
      const needsTrackingCookies = urlString.includes('/register')
        || urlString.includes('/process-premium')
        || urlString.includes('/login')
        || urlString.includes('/refresh')
        || urlString.includes('/activate-extra')

      // Set default headers
      options.headers = {
        ...options.headers,
        apporigin: baseStore.platform,
        appversion: baseStore.version,
      }

      // Add authorization header if user is logged in
      if (userStore.access_token) {
        options.headers.Authorization = `Bearer ${userStore.access_token}`
      }

      // Add tracking cookies for specific endpoints
      if (needsTrackingCookies && consentStore.advertisingAllowed) {
        const ttp = getTtpCookie()
        if (ttp) {
          options.headers['X-Tiktok-Ttp'] = ttp
        }

        const fbp = getFbpCookie()
        if (fbp) {
          options.headers['X-Meta-Fbp'] = fbp
        }

        const fbc = getFbcCookie()
        if (fbc) {
          options.headers['X-Meta-Fbc'] = fbc
        }
      }
    },
    onResponseError({ response, request }) {
      // Handle 401 errors with automatic token refresh
      if (response.status === 401 && userStore.access_token) {
        const urlString = typeof request === 'string' ? request : request.toString()

        // Only attempt refresh for non-refresh endpoints
        if (!urlString.includes('/refresh')) {
          return handleTokenRefresh(request, response)
        }
      }

      return Promise.reject(response)
    },
  })

  // Token refresh logic
  async function handleTokenRefresh(originalRequest: any, originalResponse: any) {
    try {
      const config = useRuntimeConfig()
      const refreshResponse = await $fetch(`${config.public.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${userStore.access_token}`,
        },
      })

      if (refreshResponse.success === 1) {
        // Update user data and token
        await userStore.setUser(refreshResponse.user_data)
        await userStore.setToken(refreshResponse.access_token)

        // Retry the original request with new token
        const retryOptions = {
          ...originalRequest,
          headers: {
            ...originalRequest.headers,
            Authorization: `Bearer ${refreshResponse.access_token}`,
          },
        }

        return customFetch(originalRequest, retryOptions)
      }
      else {
        // Refresh failed, clear user data
        await userStore.clearUser()
        if (import.meta.client) {
          window.location.href = '/'
        }
        return Promise.reject(originalResponse)
      }
    }
    catch (error) {
      console.error('Token refresh failed:', error)
      await userStore.clearUser()
      if (import.meta.client) {
        window.location.href = '/'
      }
      return Promise.reject(originalResponse)
    }
  }

  // Override the global $fetch
  globalThis.$fetch = customFetch

  // Make it available via provide
  return {
    provide: {
      fetch: customFetch,
    },
  }
})
